import React, { useContext, useEffect } from 'react';

import { media } from 'context';
import { Animate, Layout } from 'ds';
import { selectDashboardWidgetConfigs } from 'store/Dashboard/selectors';
import { useAppSelector } from 'store/hooks';

import Widget from './Widget';

const BASE_DELAY = 20;
const SLOT_DELAY = 30;

interface Props {}

let hasAnimated = false;

const Widgets: React.FC<Props> = () => {
  const widgetConfigs = useAppSelector(selectDashboardWidgetConfigs);
  const { isMobile } = useContext(media);

  useEffect(() => {
    setTimeout(() => {
      hasAnimated = true;
    }, 1000);
  }, []);

  if (!widgetConfigs || widgetConfigs.length === 0) return null;

  const slot1 = Array.isArray(widgetConfigs[0]) ? widgetConfigs[0] : [];
  const slot2 = Array.isArray(widgetConfigs[1]) ? widgetConfigs[1] : [];
  const slot3 = Array.isArray(widgetConfigs[2]) ? widgetConfigs[2] : [];

  if (isMobile) {
    return (
      <Layout direction="column" gap={12} width="100%">
        <WidgetsContainer>
          <WidgetContainer delay={BASE_DELAY + SLOT_DELAY}>
            {slot1[0] ? <Widget widgetConfig={slot1[0]} variant={'half'} /> : null}
          </WidgetContainer>
        </WidgetsContainer>
        <WidgetsContainer>
          <WidgetContainer delay={BASE_DELAY + SLOT_DELAY * 2}>
            {slot2[0] ? <Widget widgetConfig={slot2[0]} variant={'half'} /> : null}
            {slot3[0] ? <Widget widgetConfig={slot3[0]} variant={'half'} /> : null}
          </WidgetContainer>
        </WidgetsContainer>
      </Layout>
    );
  }

  return (
    <Layout height={168} gap={8} flex width="100%">
      <WidgetsContainer>
        {slot1.map((widgetConfig, index) => (
          <WidgetContainer delay={BASE_DELAY + index * SLOT_DELAY} key={widgetConfig.type + index}>
            <Widget widgetConfig={widgetConfig} variant={slot1.length === 1 ? 'full' : 'half'} />
          </WidgetContainer>
        ))}
      </WidgetsContainer>
      <WidgetsContainer>
        {slot2.map((widgetConfig, index) => (
          <WidgetContainer delay={BASE_DELAY + SLOT_DELAY * 2 + index * SLOT_DELAY} key={widgetConfig.type + index}>
            <Widget widgetConfig={widgetConfig} variant={slot2.length === 1 ? 'full' : 'half'} />
          </WidgetContainer>
        ))}
      </WidgetsContainer>
      <WidgetsContainer>
        {slot3.map((widgetConfig, index) => (
          <WidgetContainer delay={BASE_DELAY + SLOT_DELAY * 4 + index * SLOT_DELAY} key={widgetConfig.type + index}>
            <Widget widgetConfig={widgetConfig} variant={slot3.length === 1 ? 'full' : 'half'} />
          </WidgetContainer>
        ))}
      </WidgetsContainer>
    </Layout>
  );
};

function WidgetContainer({ children, delay }: { children: React.ReactNode; delay?: number }) {
  const { isMobile } = useContext(media);
  if (hasAnimated)
    return (
      <Layout
        minWidth={0}
        height="100%"
        direction={isMobile ? 'row' : undefined}
        width={isMobile ? '100%' : undefined}
        gap={isMobile ? 12 : undefined}
      >
        {children}
      </Layout>
    );
  return (
    <Animate
      delay={delay}
      minWidth={0}
      height="100%"
      direction={isMobile ? 'row' : undefined}
      width={isMobile ? '100%' : undefined}
      gap={isMobile ? 12 : undefined}
    >
      {children}
    </Animate>
  );
}

function WidgetsContainer({ children }: { children: React.ReactNode }) {
  const { isMobile } = useContext(media);
  return (
    <Layout
      {...(isMobile
        ? { minHeight: 72, direction: 'row', gap: 12, width: '100%' }
        : { height: '100%', direction: 'column', gap: 8, equalWidth: true, minWidth: 0 })}
    >
      {children}
    </Layout>
  );
}

export default Widgets;
